/**
 * Utility functions for historical events data
 */

/**
 * Sort historical events by start year
 */
export function sortHistoricalEvents(
  items: HistoricalEvent[]
): HistoricalEvent[] {
  return [...items].sort((a, b) => {
    const yearA = a.date?.startYear || 0;
    const yearB = b.date?.startYear || 0;
    return yearA - yearB;
  });
}

/**
 * Get historical event by ID
 */
export function getHistoricalEventById(
  items: HistoricalEvent[],
  id: string
): HistoricalEvent | undefined {
  return items.find(item => item.id === id);
}

/**
 * Get historical event by title
 */
export function getHistoricalEventByTitle(
  items: HistoricalEvent[],
  title: string
): HistoricalEvent | undefined {
  return items.find(item => item.title === title);
}

/**
 * Filter historical events by importance level
 */
export function filterEventsByImportance(
  events: HistoricalEvent[],
  importance: Importance
): HistoricalEvent[] {
  return events.filter(event => event.importance === importance);
}

/**
 * Get high importance events
 */
export function getHighImportanceEvents(
  events: HistoricalEvent[]
): HistoricalEvent[] {
  return filterEventsByImportance(events, 'high');
}

/**
 * Filter events by type
 */
export function filterEventsByType(
  events: HistoricalEvent[],
  eventType: HistoricalEvent['eventType']
): HistoricalEvent[] {
  return events.filter(event => event.eventType === eventType);
}

/**
 * Filter events by region
 */
export function filterEventsByRegion(
  events: HistoricalEvent[],
  region: string
): HistoricalEvent[] {
  return events.filter(event => event.region === region);
}

/**
 * Filter events by date range
 */
export function filterEventsByDateRange(
  events: HistoricalEvent[],
  startYear: number,
  endYear: number
): HistoricalEvent[] {
  return events.filter(event => {
    const eventStart = event.date?.startYear || 0;
    const eventEnd = event.date?.endYear || eventStart;
    // Check if event overlaps with the range
    return eventStart <= endYear && eventEnd >= startYear;
  });
}

/**
 * Get events related to a specific book
 */
export function getEventsByBook(
  events: HistoricalEvent[],
  book: string
): HistoricalEvent[] {
  return events.filter(event => event.book === book);
}

/**
 * Get events related to a specific passage
 */
export function getEventsByPassage(
  events: HistoricalEvent[],
  passageId: string
): HistoricalEvent[] {
  return events.filter(event => event.passageId === passageId);
}

